import React, { useState, useEffect, useCallback } from 'react';
import { usePollingApi, fmtKST, apiFetch, holdOr } from '../hooks/useApi';

export default function RiskPanel() {
  const { data: summary, loading: sLoading, refetch: refetchSummary } = usePollingApi('/api/risk/summary', 300000);
  const { data: events, loading: eLoading, refetch: refetchEvents } = usePollingApi('/api/risk/events?limit=50', 300000);
  const [expanded, setExpanded] = useState(null);
  const [toast, setToast] = useState(null);

  // 페이지 로드 시 리스크 자동 계산
  useEffect(() => {
    apiFetch('/api/risk/calculate', { method: 'POST' }).catch(() => {});
  }, []);

  const handleResolve = useCallback(async (eventId) => {
    try {
      await apiFetch(`/api/risk/resolve/${eventId}`, { method: 'POST' });
      setToast('리스크 해결 처리 완료');
      refetchEvents();
      refetchSummary();
      setTimeout(() => setToast(null), 3000);
    } catch {
      setToast('처리 실패');
      setTimeout(() => setToast(null), 3000);
    }
  }, [refetchEvents, refetchSummary]);

  const handleRecalc = useCallback(async () => {
    await apiFetch('/api/risk/calculate', { method: 'POST' });
    refetchEvents();
    refetchSummary();
    setToast('리스크 재계산 완료');
    setTimeout(() => setToast(null), 3000);
  }, [refetchEvents, refetchSummary]);

  const unknown = sLoading || !summary || summary.quality === 'UNKNOWN'
    || summary.critical_count == null || summary.warning_count == null;
  const critical = unknown ? null : summary.critical_count;
  const warning = unknown ? null : summary.warning_count;
  const good = !unknown && critical === 0 && warning === 0;

  return (
    <div className="space-y-4">
      {/* 상단 요약 카드 3개 */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <SummaryCard
          label="위험" count={critical}
          color={critical > 0 ? 'border-red-500 text-red-400' : 'border-gray-700 text-gray-500'}
          bgColor={critical > 0 ? 'bg-red-900/20' : 'bg-gray-800'}
        />
        <SummaryCard
          label="주의" count={warning}
          color={warning > 0 ? 'border-yellow-500 text-yellow-400' : 'border-gray-700 text-gray-500'}
          bgColor={warning > 0 ? 'bg-yellow-900/20' : 'bg-gray-800'}
        />
        <SummaryCard
          label="양호" count={unknown ? null : good ? 1 : 0}
          color={good ? 'border-green-500 text-green-400' : 'border-gray-700 text-gray-500'}
          bgColor={good ? 'bg-green-900/20' : 'bg-gray-800'}
          suffix={good ? 'All Clear' : ''}
        />
      </div>
      {unknown && <div className="text-xs text-yellow-300">판정 보류 · {summary?.reason || '리스크 관측 대기'}</div>}

      {/* Epoch 일간 HWM/MDD + 정지 */}
      {!sLoading && summary && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="text-sm font-semibold text-gray-300 mb-2">Epoch 일간 기준 HWM / MDD</div>
            <div className="text-xs text-gray-400 space-y-1 mb-3">
              <div>상태: <span className={summary.quality === 'KNOWN' ? 'text-green-400' : 'text-yellow-300'}>{summary.quality || 'UNKNOWN'}</span></div>
              <div className="break-all">Epoch: {summary.epoch_id || '—'}</div>
              <div>시작: {summary.effective_at_utc ? fmtKST(summary.effective_at_utc) : '—'}</div>
              <div>최근 관측: {summary.as_of_utc ? fmtKST(summary.as_of_utc) : '—'}</div>
              <div>방법 / 기준: {summary.method || '—'} / {summary.basis || '—'}</div>
              {summary.reason && <div className="text-yellow-300 break-words">근거: {summary.reason}</div>}
            </div>
            <div className="space-y-2">
              {(summary.portfolio_mdd || []).map((m) => (
                <div key={m.portfolio_id} className="border-t border-gray-700 pt-2 text-xs">
                  <div className="flex justify-between gap-2 mb-1">
                    <span className="text-gray-300">{m.portfolio_id} · {m.bot_name}</span>
                    <span className={m.quality === 'KNOWN' ? 'text-green-400' : m.quality === 'NOT_APPLICABLE' ? 'text-gray-400' : 'text-yellow-300'}>{m.quality || 'UNKNOWN'}</span>
                  </div>
                  <div className="text-gray-500 mb-1">관측 {m.as_of_utc ? fmtKST(m.as_of_utc) : '—'} · 표본 {m.mdd_status === 'NOT_APPLICABLE' ? 'N/A' : `${m.sample_count ?? 0}개`}</div>
                  {((m.skipped_samples || []).length > 0) && <div className="text-yellow-300 break-words mb-1">누락 표본 {m.skipped_samples.length}일 건너뜀 — 해당 일의 낙폭은 반영되지 않아 MDD가 실제보다 작을 수 있음</div>}
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-gray-400">
                    <span>현재 자본</span><span>{holdOr(m.current_capital, (v) => `₩${Number(v).toLocaleString('ko-KR')}`)}</span>
                    <span>Flow-adjusted HWM</span><span>{holdOr(m.peak_capital, (v) => `₩${Number(v).toLocaleString('ko-KR')}`)}</span>
                    <span>현재 낙폭</span><span>{holdOr(m.current_drawdown_pct, (v) => `${Number(v).toFixed(2)}%`)}</span>
                    <span>Epoch 최대 낙폭</span><span className={m.mdd_status === 'NOT_APPLICABLE' ? 'text-gray-400' : m.mdd_pct == null ? 'text-yellow-300' : m.mdd_pct < -5 ? 'text-red-400' : 'text-gray-300'}>{m.mdd_status === 'NOT_APPLICABLE' ? 'N/A' : holdOr(m.mdd_pct, (v) => `${Number(v).toFixed(2)}%`)}</span>
                  </div>
                  {m.reason && <div className="mt-1 text-yellow-300 break-words">{m.reason}</div>}
                </div>
              ))}
              {(!summary.portfolio_mdd || summary.portfolio_mdd.length === 0) && <div className="text-xs text-yellow-300">D8 관측값 없음 · {summary.reason || '리스크 관측 대기'}</div>}
            </div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="text-xs text-gray-500 mb-2">정지된 포트폴리오</div>
            {(summary.suspended_portfolios || []).length === 0 ? (
              <div className="text-xs text-green-400">없음 (정상)</div>
            ) : summary.suspended_portfolios.map((s) => (
              <div key={s.portfolio_id} className="text-xs text-red-400 mb-1">
                {s.bot_name}: {s.reason || '사유 없음'}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 이벤트 리스트 */}
      <div className="bg-gray-800 rounded-lg border border-gray-700">
        <div className="p-3 border-b border-gray-700 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-400">리스크 이벤트</h3>
          <button onClick={handleRecalc}
            className="text-xs bg-gray-700 hover:bg-gray-600 px-2 py-1 rounded transition">
            재계산
          </button>
        </div>

        {eLoading && !events ? (
          <div className="p-4 text-sm text-gray-500">로딩 중...</div>
        ) : !events || events.length === 0 ? (
          <div className={`p-4 text-sm ${unknown ? 'text-yellow-300' : 'text-green-400'}`}>
            {unknown ? '현재 재기준의 리스크 이벤트 판정 보류' : '리스크 이벤트 없음 (정상)'}
          </div>
        ) : (
          <div className="divide-y divide-gray-700/50">
            {events.map((ev) => (
              <div key={ev.id || ev.created_at}>
                <div
                  onClick={() => setExpanded(expanded === ev.id ? null : ev.id)}
                  className="p-3 flex items-start gap-3 cursor-pointer hover:bg-gray-750 transition"
                >
                  <span className={`mt-0.5 w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                    ev.severity === 'CRITICAL' ? 'bg-red-500' :
                    ev.severity === 'WARNING' ? 'bg-yellow-500' : 'bg-blue-500'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-xs flex-wrap">
                      <span className="font-medium">{ev.bot_name}</span>
                      <span className={`px-1.5 py-0.5 rounded ${
                        ev.severity === 'CRITICAL' ? 'bg-red-900 text-red-300' :
                        ev.severity === 'WARNING' ? 'bg-yellow-900 text-yellow-300' :
                        'bg-gray-700 text-gray-300'
                      }`}>{ev.severity}</span>
                      {ev.ticker && <span className="text-blue-400">{ev.ticker}</span>}
                      {ev.resolved && <span className="text-green-400">해결됨</span>}
                    </div>
                    <div className="text-xs text-gray-400 mt-0.5 line-clamp-1">{ev.description}</div>
                  </div>
                  <div className="flex-shrink-0 text-right">
                    {ev.value != null && (
                      <div className="text-xs text-red-400">{Number(ev.value).toFixed(1)}%</div>
                    )}
                    <div className="text-xs text-gray-600">{fmtKST(ev.created_at)}</div>
                  </div>
                </div>

                {/* 펼침 상세 */}
                {expanded === ev.id && (
                  <div className="px-3 pb-3 ml-5 border-l-2 border-gray-700">
                    <div className="text-xs text-gray-300 mb-2">{ev.description}</div>
                    <div className="grid grid-cols-3 gap-2 text-xs mb-3">
                      <div className="bg-gray-700/50 rounded p-2">
                        <div className="text-gray-500">수익률</div>
                        <div className="text-red-400 font-medium">{ev.value != null ? `${Number(ev.value).toFixed(1)}%` : '-'}</div>
                      </div>
                      <div className="bg-gray-700/50 rounded p-2">
                        <div className="text-gray-500">유형</div>
                        <div className="text-gray-300 font-medium">{ev.event_type}</div>
                      </div>
                      <div className="bg-gray-700/50 rounded p-2">
                        <div className="text-gray-500">발생일</div>
                        <div className="text-gray-300 font-medium">{fmtKST(ev.created_at)}</div>
                      </div>
                    </div>
                    {!ev.resolved && (
                      <div className="flex gap-2">
                        <button onClick={() => handleResolve(ev.id)}
                          className="px-3 py-1.5 bg-green-800 hover:bg-green-700 rounded text-xs min-h-[36px] transition">
                          리스크 해소
                        </button>
                        <button onClick={handleRecalc}
                          className="px-3 py-1.5 bg-blue-800 hover:bg-blue-700 rounded text-xs min-h-[36px] transition">
                          재분석
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {toast && (
        <div className="fixed bottom-4 right-4 px-4 py-3 rounded-lg shadow-xl text-sm z-50 bg-green-800 text-green-100">
          {toast}
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, count, color, bgColor, suffix }) {
  return (
    <div className={`${bgColor} rounded-lg p-4 border ${color.split(' ')[0]}`}>
      <div className="text-xs text-gray-500 mb-1">{label}</div>
      <div className={`text-3xl font-bold ${color.split(' ').pop()}`}>{count == null ? '—' : count}</div>
      {suffix && <div className="text-xs text-green-400 mt-1">{suffix}</div>}
    </div>
  );
}
